import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";

function getHasContractByType(
  isPull,
  isPullPress,
  isPress,
  hasGymContract,
  hasWorkflowContract
) {
  if (isPull) return hasWorkflowContract;
  if (isPullPress) return hasGymContract || hasWorkflowContract;
  if (isPress) return hasGymContract;
  return false;
}

export const ViewContractButton = ({ athlete, api }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [signedOnDate, setSignedOnDate] = useState(null);
  const { activeUserToRequest, activeUser, query = {}, workflows = [] } = locals ?? {};
  const section = query.section ?? "";

  const coach = activeUserToRequest ?? activeUser ?? {};
  const { pressCustomBranding } = coach;
  const hasGymContract = Boolean(
    pressCustomBranding?.gymContract?.trim?.()
  );
  const coachId = coach?._id;
  const athleteId = athlete?._id ?? athlete?.customer ?? "";
  const athleteName = athlete?.name ?? athlete?.customer_name ?? "";
  const athleteEmail = athlete?.email ?? athlete?.customer_email ?? "";
  const isPull = !!athlete?.coach?._id;
  const isPullPress = isPull && !!athlete?.isGymMember;
  const isPress = !!(athlete?.customer && athlete?.plan);
  const isTestAthlete = athleteEmail.includes("+push");
  const pullWorkflow = workflows.find((w) => w.type === "getting-started" && w.access === "pull")
  const hasWorkflowContract =
    pullWorkflow?.options?.contract?.status === "published" &&
    Boolean(pullWorkflow?.options?.contract?.contract?.trim?.());
  const hasContractByType = getHasContractByType(
    isPull,
    isPullPress,
    isPress,
    hasGymContract,
    hasWorkflowContract
  );
  const canViewContract = !isTestAthlete && hasContractByType;

  /** 
   * Load the contract PDF when the modal is opened.
   */
  useEffect(() => {
    if (!modalOpen || !api || !athleteId) return;
    setLoading(true);
    setError(null);
    setPdfPreviewUrl(null);
    setPdfBlob(null);
    setSignedOnDate(null);
    api("contract-export", { query: { athleteId, coachId } })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data?.message || data?.error || res.statusText || t("viewContract.failedToLoad"));
          return;
        }
        const contentType = res.headers.get("Content-Type") || "";
        if (contentType.includes("application/pdf")) {
          const blob = await res.blob();
          const signedOn = res.headers.get("X-Contract-Signed-On") || null;
          setPdfBlob(blob);
          setSignedOnDate(signedOn);
          setPdfPreviewUrl(URL.createObjectURL(blob));
        } else {
          setError(t("viewContract.invalidResponse"));
        }
      })
      .catch((e) => {
        setError(e?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [modalOpen, api, athleteId]);

  /**
   * Open the modal.
   */
  const handleOpen = (e) => {
    e.preventDefault();
    setError(null);
    setModalOpen(true);
  };

  /**
   * Close the modal.
   */
  const handleClose = (e) => {
    e?.preventDefault?.();
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfBlob(null);
    setSignedOnDate(null);
    setModalOpen(false);
    setError(null);
  };

  /**
   * Get the contract filename.
   */
  const getContractFilename = () => {
    const safeName = (athleteName || "Client")
      .replaceAll(/[\s/\\:*?"<>|]+/g, "_")
      .trim() || "Client";
    const dateStr = new Date().toISOString().slice(0, 10);
    return `Contract_${safeName}_${dateStr}.pdf`;
  };

  /**
   * Print the contract.
   */
  const handlePrint = () => {
    if (!pdfPreviewUrl) return;
    const printWin = window.open(pdfPreviewUrl, "_blank", "noopener");
    if (printWin) {
      setTimeout(() => {
        try {
          if (!printWin.closed) printWin.print();
        } catch (err) {
          console.warn("Contract print failed (window may be closed or blocked):", err?.message ?? err);
        }
      }, 800);
    }
  };

  /**
   * Download the contract.
   */
  const handleDownload = () => {
    if (!pdfBlob) {
      setError(t("viewContract.exportUnavailable"));
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = getContractFilename();
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canViewContract) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={`view-contract-trigger ${section === "dashboard" ? "" : "force-mobile-hidden"}`}
        data-view-contract
        data-button-type="secondary"
        onClick={handleOpen}
      >
        <SvgIcon
          src="/img/new-icons/vuesax-linear-document-text.svg"
          alt=""
        />
        {t("viewContract.trigger")}
      </button>

      {modalOpen &&
        createPortal(
          <Modal
            title={t("viewContract.modalTitle")}
            header={true}
            onClose={handleClose}
            toClear={[]}
          >
            <div className="contract-export-modal body">
              <div
                className="contract-preview-placeholder"
              >
                {loading && !error && (
                  <div className="contract-export-status contract-export-loading" aria-busy="true" />
                )}
                {error && (
                  <div className="contract-export-status">
                    <p role="alert">
                      {error}
                    </p>
                  </div>
                )}
                {pdfPreviewUrl && !error && (
                  <iframe
                    title="Contract preview"
                    src={`${pdfPreviewUrl}#toolbar=0`}
                  />
                )}
              </div>

              <div className="contract-information">
                <div className="contract-information-container">
                  <span className="contract-information-label">{t("viewContract.athleteName")}</span>
                  <span className="contract-information-value">{athleteName}</span>
                </div>
                <div className="contract-information-container">
                  <span className="contract-information-label">{t("viewContract.athleteEmail")}</span>
                  <span className="contract-information-value">{athleteEmail}</span>
                </div>
                <div className="contract-information-container">
                  <span className="contract-information-label">{t("viewContract.customerId")}</span>
                  <span className="contract-information-value">{athleteId}</span>
                </div>
                <div className="contract-information-container">
                  <span className="contract-information-label">{t("viewContract.contractSignedOn")}</span>
                  <span className="contract-information-value">{loading ? "" : (signedOnDate ?? t("viewContract.pendingSignature"))}</span>
                </div>

                <div className="contract-information-actions">
                  <button
                    type="button"
                    className="download-contract-button"
                    data-button-type="secondary"
                    onClick={handleDownload}
                    disabled={loading || !pdfBlob}
                  >
                    <SvgIcon
                      src="/img/new-icons/vuesax-linear-document-text.svg"
                      alt=""
                    />
                    {t("viewContract.download")}
                  </button>
                  <button
                    disabled={loading || !pdfBlob}
                    type="button"
                    className="print-contract-button"
                    data-button-type="secondary"
                    onClick={handlePrint}
                  >
                    <SvgIcon
                      src="/img/new-icons/vuesax-linear-document-text.svg"
                      alt=""
                    />
                    {t("viewContract.print")}
                  </button>
                </div>
              </div>
            </div>

          </Modal>,
          document.body
        )}
    </>
  );
};

ViewContractButton.propTypes = {
  athlete: PropTypes.object,
  api: PropTypes.func,
};
